import { forwardRef, useImperativeHandle } from 'react';
import { TabSaveHandler } from '.';
import { DarwinFlex, DarwinSpin } from 'components/darwin-ui';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  EditProcessBlockPayload,
  EditProcessBlockForm,
} from 'types/processBlockTypes';
import { ConfigureFormSection } from './ConfigureFormSection';
import { Form } from 'antd';
import styles from './ProcessBlock.module.css';
import {
  useGetProcessBlockById,
  useUpdateProcessBlockId,
} from 'api/services/processMapService';
import { useUI } from 'contexts/UiContext';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'routes/routes';
import { ErrorType } from 'types/genericTypes';

interface ProcessBlockConfigureProps {
  onNameChange?: (name: string) => void;
}

const enum BoundaryProcessType {
  RECEIVING = 'Receiving',
  SHIPPING = 'Shipping',
}

export const ProcessBlockConfigure = forwardRef<
  TabSaveHandler,
  ProcessBlockConfigureProps
>(({ onNameChange }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openNotification } = useFloatNotification();
  const { selectedCompany } = useUI();
  //Get processflow data
  const { id: traceabilityPlanId, processId: processFlowId } = useParams();

  const { data: isProcessBlockData, isLoading: isLoadingProcessBlockData } =
    useGetProcessBlockById({
      companyId: selectedCompany || '',
      traceabilityPlanId: traceabilityPlanId || '',
      processFlowId: processFlowId || '',
    });

  //Verify if the process is checked as end flow
  const isChecked = isProcessBlockData?.data.isEndOfFlow || false;

  const isCheckedInitialFlow = isProcessBlockData?.data.isInitOfFlow || false;

  //Verify if the process is an end flow
  const endFlow =
    isProcessBlockData?.data.process.name === BoundaryProcessType.RECEIVING ||
    isProcessBlockData?.data.process.name === BoundaryProcessType.SHIPPING;

  //Verify if the process is an initial flow
  const initialFlow =
    isProcessBlockData?.data.process.name === BoundaryProcessType.RECEIVING;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<EditProcessBlockForm>({
    mode: 'onChange',
  });

  const name = watch('name');

  //Call parent function for render name
  useEffect(() => {
    onNameChange?.(name);
  }, [name, onNameChange]);

  const handleNavigate = () => {
    navigate(
      generatePath(ROUTES.PROCESSES.MAPS.MAP.EDIT, {
        id: traceabilityPlanId || '',
      })
    );
  };

  const {
    mutateAsync: isUpdatingProcessBlock,
    isPending: isPendingProcessBlock,
  } = useUpdateProcessBlockId();

  const onSubmit =
    (saveAndExit: boolean): SubmitHandler<EditProcessBlockPayload> =>
    async data => {
      const { name, description, isEndOfFlow, isInitOfFlow } = data;

      const payload: EditProcessBlockPayload = {
        name,
        description,
        isEndOfFlow: !!isEndOfFlow,
        isInitOfFlow: !!isInitOfFlow,
      };

      try {
        await isUpdatingProcessBlock({
          companyId: selectedCompany || '',
          traceabilityPlanId: traceabilityPlanId || '',
          processFlowId: processFlowId || '',
          payload,
        });

        openNotification(
          t('success'),
          t('process-flow.configure.updated-success'),
          'success'
        );
        if (saveAndExit) handleNavigate();
      } catch (err) {
        const apiError = err as ErrorType;
        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('process-flow.configure.updated-error'),
            'error'
          );
        }
      }
    };

  useImperativeHandle(ref, () => ({
    save: (saveAndExit?: boolean) => handleSubmit(onSubmit(!!saveAndExit))(),
  }));

  //Logic for set data into inputs
  useEffect(() => {
    if (isProcessBlockData?.data) {
      const dataProcess = {
        name: isProcessBlockData.data.name || '',
        description: isProcessBlockData.data.description || '',
        inputs: isProcessBlockData.data.inputs.filter(
          process => process.type === 'normal'
        ),
        processType: isProcessBlockData.data.process.name,
        isEndOfFlow: isProcessBlockData.data.isEndOfFlow,
        isInitOfFlow: isProcessBlockData.data.isInitOfFlow,
      };

      const {
        name,
        description,
        inputs,
        processType,
        isEndOfFlow,
        isInitOfFlow,
      } = dataProcess;

      const inputNames = inputs?.map(input => input.name || '');
      const viewOrEditPayload = {
        name,
        description,
        inputs: inputNames.join(', '),
        processType: processType,
        isEndOfFlow: isEndOfFlow || false,
        isInitOfFlow: isInitOfFlow || false,
      };
      reset(viewOrEditPayload);
    }
  }, [reset, isProcessBlockData]);

  return (
    <>
      <DarwinSpin
        fullscreen
        spinning={isPendingProcessBlock || isLoadingProcessBlockData}
      />
      <DarwinFlex vertical className={styles.processBlockFormRow}>
        <Form layout="vertical">
          <ConfigureFormSection
            control={control}
            errors={errors}
            endOfFlow={endFlow}
            initialFlow={initialFlow}
            isCheckedEndOfFlow={isChecked}
            isCheckedInitialFlow={isCheckedInitialFlow}
          />
        </Form>
      </DarwinFlex>
    </>
  );
});
